import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Eye, Search, CheckSquare, Square, MessageSquare, X, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTableTools } from "@/lib/useTableTools";
import { exportCsv } from "@/lib/exportCsv";
import { BulkBar } from "@/components/BulkBar";
import { money } from "@/lib/format";
import { flagUrl } from "@/i18n/translations";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = { client_name: "", deceased_name: "", service_code: "", transport_type_code: "", origin: "", destination: "", distance_km: 0, client_email: "" };
const ORDER_STATUS = [
  { value: "pending", label: "ostatus.pending" }, { value: "confirmed", label: "ostatus.confirmed" },
  { value: "in_transit", label: "ostatus.in_transit" }, { value: "delivered", label: "ostatus.delivered" },
  { value: "cancelled", label: "ostatus.cancelled" },
];

const CountryFlag = ({ cc, name, ip }) => {
  if (!cc || cc.length !== 2) return null;
  return (
    <img src={flagUrl(cc.toLowerCase(), 20)} alt={cc} loading="lazy"
      className="h-3.5 w-5 rounded-[2px] object-cover shadow-sm ring-1 ring-black/5"
      title={`${name || cc}${ip ? " · " + ip : ""}`} />
  );
};

export default function Orders() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get("filter");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [settings, setSettings] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [assign, setAssign] = useState({ partners: [], drivers: [], vehicles: [] });
  const [unreadByOrder, setUnreadByOrder] = useState({});
  const [view, setView] = useState("active");
  const [filterSets, setFilterSets] = useState({ unread_partner: null, unread_driver: null, docs: null });
  const isStaff = ["admin", "support"].includes(user?.role);
  const canSeePayment = !["driver", "partner"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/orders"); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/chat/unread-by-order").then(({ data }) => setUnreadByOrder(data || {})).catch(() => {});
  }, [load]);
  useEffect(() => {
    if (!["admin", "support"].includes(user?.role)) return;
    Promise.all([api.get("/partners"), api.get("/drivers"), api.get("/vehicles")])
      .then(([p, d, v]) => setAssign({
        partners: p.data.map((x) => ({ value: x.id, label: x.company_name })),
        drivers: d.data.map((x) => ({ value: x.id, label: x.name })),
        vehicles: v.data.map((x) => ({ value: x.id, label: x.plate })),
      })).catch(() => {});
    Promise.all([api.get("/chat/unread-summary"), api.get("/documents/pending-summary")])
      .then(([s, d]) => {
        const partner = new Set(), driver = new Set();
        (s.data.orders || []).forEach((o) => (o.channel === "company" ? partner : driver).add(o.order_id));
        const docs = new Set((d.data.orders || []).map((o) => o.order_id));
        setFilterSets({ unread_partner: partner, unread_driver: driver, docs });
      }).catch(() => {});
  }, [user]);
  const name = (obj) => obj?.name?.[locale] || obj?.name?.en || obj?.code;

  const remindMandate = async (e, id) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/orders/${id}/mandate/remind`);
      toast.success(`${t("mand.reminderSent")} (${data.count})`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const baseRows = useMemo(() => rows.filter((o) => {
    const d = (o.created_at || "").slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    const done = ["delivered", "cancelled"].includes(o.status);
    if (view === "active" && done) return false;
    if (view === "done" && !done) return false;
    if (activeFilter === "unread" && !(unreadByOrder[o.id] > 0)) return false;
    if (activeFilter === "unread_partner" && !(filterSets.unread_partner?.has(o.id))) return false;
    if (activeFilter === "unread_driver" && !(filterSets.unread_driver?.has(o.id))) return false;
    if (activeFilter === "docs" && !(filterSets.docs?.has(o.id))) return false;
    return true;
  }), [rows, dateFrom, dateTo, activeFilter, unreadByOrder, filterSets, view]);
  const activeCount = rows.filter((o) => !["delivered", "cancelled"].includes(o.status)).length;
  const doneCount = rows.filter((o) => ["delivered", "cancelled"].includes(o.status)).length;

  const tools = useTableTools(baseRows, [
    "number", "client_name", "client_phone", "client_email", "origin", "destination",
    "partner_id", "driver_id", "vehicle_id", "status", "payment_status", "invoice_number",
  ]);

  const BULK_COLUMNS = [
    { key: "number", label: t("common.number") }, { key: "client_name", label: t("order.fClient") },
    { key: "origin", label: t("order.fOrigin") }, { key: "destination", label: t("order.fDest") },
    { key: "status", label: t("common.status") }, { key: "payment_status", label: t("order.payment") },
    { key: "invoice_number", label: t("common.invoice") }, { key: "total", label: t("common.total") },
  ];

  const onBulk = async (action, value) => {
    const ids = [...tools.selected];
    if (action === "export") { exportCsv(tools.selectedRows, BULK_COLUMNS, "orders.csv"); return; }
    if (action === "delete" && !window.confirm(`${t("common.delete")} ${ids.length}?`)) return;
    try {
      const { data } = await api.post("/orders/bulk", { ids, action, value });
      toast.success(`✓ ${data.deleted ?? data.updated ?? 0}`);
      tools.clear(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const create = async () => {
    try { const { data } = await api.post("/orders", { ...form, client_email: form.client_email || null, distance_km: Number(form.distance_km || 0) }); toast.success(`${t("order.created")} ${data.number}`); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("menu.orders")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("sub.orders")}</p>
        </div>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-blue-900 hover:bg-blue-800" data-testid="order-create-btn"><Plus size={16} className="mr-1.5" /> {t("common.create")}</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{t("order.new")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="space-y-1.5"><Label>{t("order.fClient")}</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="order-client-name" /></div>
                <div className="space-y-1.5"><Label>{t("order.fClientEmail")}</Label><Input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="order-client-email" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("order.fDeceased")}</Label><Input value={form.deceased_name} onChange={(e) => setForm({ ...form, deceased_name: e.target.value })} data-testid="order-deceased" /></div>
                <div className="space-y-1.5"><Label>{t("order.fService")}</Label>
                  <Select value={form.service_code} onValueChange={(v) => setForm({ ...form, service_code: v })}>
                    <SelectTrigger data-testid="order-service"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{settings?.services?.map((s) => <SelectItem key={s.code} value={s.code}>{name(s)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>{t("order.fTransport")}</Label>
                  <Select value={form.transport_type_code} onValueChange={(v) => setForm({ ...form, transport_type_code: v })}>
                    <SelectTrigger data-testid="order-transport"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{settings?.transport_types?.map((s) => <SelectItem key={s.code} value={s.code}>{name(s)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>{t("order.fOrigin")}</Label><Input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="order-origin" /></div>
                <div className="space-y-1.5"><Label>{t("order.fDest")}</Label><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="order-destination" /></div>
                <div className="space-y-1.5"><Label>{t("order.fDistance")}</Label><Input type="number" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="order-distance" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={create} className="bg-blue-900 hover:bg-blue-800" data-testid="order-save-btn">{t("common.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200" data-testid="orders-view-tabs">
        <button onClick={() => setView("active")} data-testid="orders-tab-active"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "active" ? "border-blue-900 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          {t("order.tabActive")} <span className="ml-1 text-xs text-slate-400">({activeCount})</span>
        </button>
        <button onClick={() => setView("done")} data-testid="orders-tab-done"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "done" ? "border-blue-900 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          {t("order.tabDone")} <span className="ml-1 text-xs text-slate-400">({doneCount})</span>
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={tools.query} onChange={(e) => tools.setQuery(e.target.value)} placeholder={t("ph.orders")} className="pl-9" data-testid="orders-search" />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-auto" data-testid="orders-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-auto" data-testid="orders-date-to" />
        <Button variant="outline" size="sm" onClick={tools.selectAllVisible} data-testid="orders-select-all"><CheckSquare size={15} className="mr-1.5" /> {t("common.selectAll")}</Button>
        <Button variant="outline" size="sm" onClick={tools.clear} data-testid="orders-clear"><Square size={15} className="mr-1.5" /> {t("common.deselect")}</Button>
        <span className="text-sm text-slate-400 ml-auto">{tools.filtered.length} / {rows.length}</span>
      </div>

      {activeFilter && (
        <div className="flex items-center gap-2" data-testid="orders-active-filter">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs px-3 py-1">
            <MessageSquare size={12} /> {activeFilter}
            <button onClick={() => setSearchParams({})} className="hover:text-blue-950" data-testid="orders-clear-filter"><X size={13} /></button>
          </span>
          <span className="text-xs text-slate-400">{t("orders.filterClear")}</span>
        </div>
      )}

      {isStaff && tools.selected.size > 0 && (
        <BulkBar
          count={tools.selected.size}
          role={user?.role}
          statusOptions={ORDER_STATUS}
          assignOptions={assign.partners}
          actions={[
            { key: "export", kind: "export", action: "export", label: "bulk.export", roles: ["admin", "support"] },
            { key: "status", kind: "status", label: "bulk.status", roles: ["admin", "support"] },
            { key: "assign", kind: "assign", label: "bulk.assignCompany", roles: ["admin"] },
            { key: "delete", kind: "delete", action: "delete", label: "bulk.delete", roles: ["admin"] },
          ]}
          onAction={onBulk}
          onClear={tools.clear}
        />
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            {isStaff && <TableHead className="w-10"><Checkbox checked={tools.allVisibleSelected} onCheckedChange={(v) => v ? tools.selectAllVisible() : tools.clear()} data-testid="orders-header-checkbox" /></TableHead>}
            <TableHead className="text-xs uppercase text-slate-500">{t("common.number")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.tracking")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.client")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.route")}</TableHead>
            {canSeePayment && <TableHead className="text-xs uppercase text-slate-500">{t("common.invoice")}</TableHead>}
            {canSeePayment && <TableHead className="text-xs uppercase text-slate-500">{t("order.payment")}</TableHead>}
            {canSeePayment && <TableHead className="text-xs uppercase text-slate-500">{t("common.total")}</TableHead>}
            <TableHead className="text-xs uppercase text-slate-500">{t("common.status")}</TableHead>
            {isStaff && <TableHead className="text-xs uppercase text-slate-500">{t("mand.title")}</TableHead>}
            <TableHead className="text-xs uppercase text-slate-500 text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">{t("common.loading")}</TableCell></TableRow>
              : tools.filtered.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">{t("common.none")}</TableCell></TableRow>
              : tools.filtered.map((o) => (
                <TableRow key={o.id} data-testid="order-row" className="cursor-pointer" onClick={() => navigate(`/orders/${o.id}`)}>
                  {isStaff && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={tools.selected.has(o.id)} onCheckedChange={() => tools.toggle(o.id)} data-testid="orders-row-checkbox" /></TableCell>}
                  <TableCell className="font-medium text-slate-800">
                    <span className="inline-flex items-center gap-1.5">
                      {o.number}
                      {unreadByOrder[o.id] > 0 && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5" data-testid="order-unread-badge">
                          <MessageSquare size={10} /> {unreadByOrder[o.id]}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{o.tracking_code}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {o.client_country && (
                        <span data-testid="order-flag"><CountryFlag cc={o.client_country} name={o.client_country_name} ip={o.client_ip} /></span>
                      )}
                      {o.client_name || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{o.origin} → {o.destination}</TableCell>
                  {canSeePayment && <TableCell className="text-sm">{o.invoice_number || "—"}</TableCell>}
                  {canSeePayment && (
                  <TableCell>
                    {o.payment_status === "paid" ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="order-paid-badge">{t("order.paid")}</span>
                    ) : (
                      <span className="text-xs text-slate-400">{t("order.unpaid")}</span>
                    )}
                  </TableCell>
                  )}
                  {canSeePayment && <TableCell className="font-medium">{money(o.total, o.currency)}</TableCell>}
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  {isStaff && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {o.mandate_status ? (
                        <div className="flex items-center gap-2" data-testid="order-mandate-cell">
                          {o.mandate_status === "signed" ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="order-mandate-signed">{t("mand.st.signed")}</span>
                          ) : (
                            <>
                              <span className="text-xs font-semibold text-amber-600" data-testid="order-mandate-pct">{o.mandate_completion ?? 0}%</span>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50" onClick={(e) => remindMandate(e, o.id)} data-testid="order-mandate-remind" title={t("mand.remind")}>
                                <Send size={13} />
                              </Button>
                            </>
                          )}
                        </div>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </TableCell>
                  )}
                  <TableCell className="text-right"><Button size="icon" variant="ghost" data-testid="order-view-btn"><Eye size={15} /></Button></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
